import { useState, useEffect } from "react";
import PlatformBrand from "../components/PlatformBrand";
import { useNavigate, Link, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sun, Moon } from "@phosphor-icons/react";
import { DoorInstallPrompt } from "../components/DoorInstallPrompt";
import { brandVars } from "../lib/brandTheme";
import { applyPortalHead } from "../lib/installPrompt";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage({ tenantPortal = null }) {
  const { login, verifyMfa, register, formatApiError } = useAuth();
  const [searchParams] = useSearchParams();
  const { brand: brandSlug } = useParams();
  const retiredLink = searchParams.get("retired") === "1";
  const nextParam = searchParams.get("next");
  const nextPath = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/";
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [clientBrand, setClientBrand] = useState(null); // {company_name, tagline, logo_public_path} for /login/:brand
  const [brandMissing, setBrandMissing] = useState(false);

  useEffect(() => {
    if (!brandSlug || tenantPortal) return;
    axios.get(`${BACKEND}/api/public/portal-brand/${encodeURIComponent(brandSlug)}`)
      .then(({ data }) => { setClientBrand(data); document.title = `${data.company_name} — Client Portal`; })
      .catch(() => setBrandMissing(true));
  }, [brandSlug, tenantPortal]);
  useEffect(() => {
    if (tenantPortal?.company_name) document.title = `${tenantPortal.company_name} — Sign in`;
  }, [tenantPortal]);
  // Client sign-in door: "Add to home screen" here installs the client's own branded app
  useEffect(() => (clientBrand?.slug ? applyPortalHead({ slug: clientBrand.slug, companyName: clientBrand.company_name, primaryColor: clientBrand.primary_color }) : undefined), [clientBrand]);
  // Tenant front door (fixlr.app/<slug>) takes precedence over a client-portal brand
  const brand = tenantPortal
    ? { company_name: tenantPortal.company_name, tagline: tenantPortal.tagline, logo_public_path: tenantPortal.logo_url, kind: "tenant" }
    : clientBrand;
  const accent = tenantPortal?.primary_color || null;
  const brandLogo = brand?.logo_public_path ? (brand.logo_public_path.startsWith("http") ? brand.logo_public_path : `${BACKEND}${brand.logo_public_path}`) : "";
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("team_member");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaEmail, setMfaEmail] = useState("");
  const [mfaToken, setMfaToken] = useState(""); // proof of the password step, expires after 5 minutes
  const [mfaCode, setMfaCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name, role);
        navigate(nextPath);
      } else {
        const result = await login(email, password, tenantPortal?.slug);
        if (result.requires_mfa) {
          setMfaStep(true);
          setMfaEmail(result.email);
          setMfaToken(result.mfa_token || "");
          setLoading(false);
          return;
        }
        navigate(nextPath);
      }
    } catch (err) {
      console.error("Login error details:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        code: err.code,
      });
      if (err.response?.data?.detail) {
        setError(formatApiError(err.response.data.detail));
      } else if (err.response?.status === 422) {
        setError("Invalid request format. Please check your email and password.");
      } else if (err.response?.status) {
        setError(`Login failed (error ${err.response.status}). Please try again.`);
      } else if (err.code === "ERR_NETWORK" || !err.response) {
        setError("Cannot reach the server. Please check your connection and try again.");
      } else {
        setError(err.message || "An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (useBackupCode) {
        await verifyMfa(mfaToken, "", mfaCode);
      } else {
        await verifyMfa(mfaToken, mfaCode, "");
      }
      navigate(nextPath);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid MFA code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isDark = theme === "dark";

  return (
    <div className="min-h-screen flex" style={brandVars(brand?.primary_color, isDark ? "dark" : "light")} data-testid="login-page">
      {/* Left panel — Fixlr charcoal with lime/blue glows; branded client login: the client's colour */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center login-panel-dark"
        style={brand?.primary_color ? { background: brand.primary_color } : undefined}
        data-testid="login-left-panel"
      >
        <div className="relative z-10 px-16 text-white">
          {brand ? (
            <div className="mb-8" data-testid="login-brand-panel">
              {brandLogo ? (
                <div className="inline-flex items-center justify-center bg-white/95 rounded-xl px-6 py-4 mb-6 shadow-lg"><img src={brandLogo} alt={brand.company_name} className="h-16 max-w-[260px] w-auto object-contain" data-testid="login-brand-logo" /></div>
              ) : (
                <div className="h-16 w-16 rounded-xl bg-white/15 flex items-center justify-center text-3xl font-bold font-[Chivo] mb-6">{brand.company_name.charAt(0)}</div>
              )}
              <h1 className="text-4xl sm:text-5xl font-bold font-[Chivo] tracking-tight mb-3 leading-tight" data-testid="login-brand-name">{brand.company_name}</h1>
              <p className="text-lg text-slate-200 font-medium">{brand.kind === "tenant" ? "Workspace sign-in" : "Client Portal"}</p>
            </div>
          ) : (
            <PlatformBrand variant="login-hero" className="mb-8" />
          )}
          {!brand && (
            <h1 className="text-4xl sm:text-5xl font-bold font-[Chivo] tracking-tight mb-4 leading-tight">
              Facilities<br />Management Platform
            </h1>
          )}
          <p className="text-base leading-relaxed max-w-md text-slate-300">
            {brand
              ? (brand.tagline || (brand.kind === "tenant"
                ? "Manage jobs, suppliers, quotes, compliance and your in-house team — all in one workspace."
                : "Raise requests, approve quotes and follow every job and project through to completion — all in one place."))
              : "Centralised platform for managing facilities maintenance, refurbishment, compliance, and planned works across your portfolio."}
          </p>
          <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {(brand && brand.kind !== "tenant" ? [
              "Job Requests", "Quote Approvals",
              "Live Projects", "Property Records",
              "Invoices", "Direct Messaging",
            ] : [
              "Project Tracking", "Supplier Management",
              "Works Orders", "Approval Workflows",
              "Compliance Tracking", "AI Assistant",
            ]).map((item, i) => (
              <div key={item} className="flex items-center gap-2.5 text-slate-400">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: accent || ["#D6EF37", "#135DD4", "#D6EF37", "#135DD4", "#D6EF37", "#135DD4"][i] }}
                />
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="login-hex-grid" />
        <div className="login-glow login-glow-orange" />
        <div className="login-glow login-glow-blue" />
        <div className="login-glow login-glow-yellow" />
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background relative">
        {accent && <div className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: accent }} data-testid="tenant-accent-bar" />}
        <div className="w-full max-w-sm animate-fade-in">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2 min-w-0">
              {brand ? (
                brandLogo ? <img src={brandLogo} alt={brand.company_name} className="h-11 max-w-[200px] w-auto object-contain" data-testid="login-brand-logo-right" /> : <span className="text-lg font-bold font-[Chivo] truncate" data-testid="login-brand-name-right">{brand.company_name}</span>
              ) : (
                <PlatformBrand variant="login-right" />
              )}
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={toggleTheme} data-testid="login-theme-toggle">
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>

          <h2 className="text-2xl font-bold font-[Chivo] tracking-tight mb-1" data-testid="login-heading">
            {isRegister ? "Create Account" : brand ? `Sign in to ${brand.company_name}` : "Sign In"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {isRegister ? "Register to get started" : brand ? (brand.kind === "tenant" ? `Use your ${brand.company_name} account` : "Use the client portal account you were invited with") : "Enter your credentials to access your account"}
          </p>

          {tenantPortal?.status && tenantPortal.status !== "active" && (
            <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-300" data-testid="tenant-status-notice">
              This workspace is currently {tenantPortal.status}. Contact your administrator or Fixlr support if you need access.
            </div>
          )}

          {brandMissing && (
            <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-300" data-testid="brand-missing-notice">
              That portal link isn't recognised — you can still sign in below with your usual account.
            </div>
          )}

          {retiredLink && (
            <div className="mb-4 p-3 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 text-xs text-blue-800 dark:text-blue-300" data-testid="retired-link-notice">
              <p className="font-semibold mb-0.5">Client portal links have been upgraded</p>
              <p>Shared links are no longer used. Sign in with your personal client account below — if you don't have one yet, ask your account manager for an invitation.</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-md border border-destructive/40 bg-destructive/10 text-destructive dark:text-red-300 text-sm" role="alert" data-testid="auth-error">
              {error}
            </div>
          )}

          {mfaStep ? (
            <form onSubmit={handleMfaVerify} className="space-y-4" data-testid="mfa-form">
              <div className="p-3 rounded-md bg-muted/50 border border-border text-center mb-2">
                <p className="text-sm font-medium mb-0.5">Two-Factor Authentication</p>
                <p className="text-[11px] text-muted-foreground">Enter the 6-digit code from your authenticator app</p>
              </div>
              <div>
                <Label>{useBackupCode ? "Backup Code" : "Authentication Code"}</Label>
                <Input
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  placeholder={useBackupCode ? "Enter backup code" : "Enter 6-digit code"}
                  className="text-center text-lg tracking-widest font-mono"
                  maxLength={useBackupCode ? 8 : 6}
                  autoFocus
                  required
                  data-testid="mfa-code-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="mfa-verify-btn">
                {loading ? "Verifying..." : "Verify"}
              </Button>
              <button
                type="button"
                className="w-full text-xs text-muted-foreground hover:text-primary"
                onClick={() => { setUseBackupCode(!useBackupCode); setMfaCode(""); }}
                data-testid="mfa-toggle-backup"
              >
                {useBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
              </button>
              <button
                type="button"
                className="w-full text-xs text-muted-foreground hover:text-primary"
                onClick={() => { setMfaStep(false); setMfaCode(""); setError(""); }}
              >
                Back to login
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" required data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="register-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team_member">Team Member</SelectItem>
                      <SelectItem value="stakeholder">Stakeholder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required data-testid="password-input" />
            </div>
            {!isRegister && (
              <div className="text-right -mt-2">
                <Link to={tenantPortal?.slug ? `/reset-password?door=${encodeURIComponent(tenantPortal.slug)}` : "/reset-password"} className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading} style={accent ? { backgroundColor: accent } : undefined} data-testid="auth-submit-btn">
              {loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}
            </Button>
          </form>
          )}
          {!brand && (
            <>
              <p className="mt-6 text-center text-sm text-muted-foreground">
                {isRegister ? "Already have an account?" : "New to Fixlr?"}{" "}
                <button
                  type="button"
                  className="text-primary font-medium hover:underline"
                  onClick={() => { setIsRegister(!isRegister); setError(""); }}
                  data-testid="toggle-auth-mode"
                >
                  {isRegister ? "Sign In" : "Create an account"}
                </button>
              </p>
              <div className="mt-3 text-center">
                <Link to="/signup" className="text-xs text-muted-foreground hover:text-primary hover:underline" data-testid="start-trial-link">
                  Start a 7-day free trial for your company
                </Link>
                <span className="text-xs text-muted-foreground mx-2">·</span>
                <Link to="/contractor/join" className="text-xs text-muted-foreground hover:text-primary hover:underline" data-testid="contractor-join-link">
                  Contractor? Advertise on the marketplace
                </Link>
              </div>
            </>
          )}
          {tenantPortal && !isRegister && !mfaStep && <DoorInstallPrompt portal={tenantPortal} />}
          {brand && !tenantPortal?.hide_platform_branding && (
            <p className="mt-8 text-[11px] text-muted-foreground flex items-center justify-center gap-1.5" data-testid="login-powered-by">
              Powered by <PlatformBrand variant="powered" />
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
